import { Transform } from 'redux-persist';
import { PersistConfig } from 'redux-persist/es/types';
import storage from 'redux-persist/lib/storage';
import { WebStorage } from 'redux-persist/lib/types';

import { createScopedStorage } from '../scopedStorage';

// Create the scoped storage wrapper once (shared across all features)
// This wrapper handles dynamic owner-based scoping at runtime
const scopedStorage = createScopedStorage(storage);

/**
 * Configuration for feature-specific persistence
 */
export interface FeaturePersistenceConfig {
  /**
   * Whether persistence is enabled for this feature
   * Defaults to false if not specified
   */
  enabled?: boolean;

  /**
   * Key to use for storing persisted state
   * Will be prefixed with 'feature_'
   */
  key?: string;

  /**
   * Storage engine to use (defaults to localStorage)
   */
  storage?: WebStorage;

  /**
   * Properties to include in persistence (if not specified, all properties are included)
   */
  whitelist?: string[];

  /**
   * Properties to exclude from persistence
   */
  blacklist?: string[];

  /**
   * Version number for migrations
   */
  version?: number;

  /**
   * Migration functions for handling version changes
   */
  migrations?: Record<number, (state: any) => any>;

  /**
   * State transformation functions
   */
  transforms?: Transform<any, any>[];

  /**
   * Merge strategy (defaults to shallow merge)
   * 'merge' = shallow merge
   * 'deepMerge' = deep merge
   * 'replace' = replace completely
   */
  mergeStrategy?: 'merge' | 'deepMerge' | 'replace';

  /**
   * Debounce time in milliseconds for persistence operations
   */
  debounce?: number;

  /**
   * Timeout in milliseconds after which persistence operations will be canceled
   */
  timeout?: number;

  /**
   * Whether to scope storage by user/org/environment (defaults to true)
   * Set to false to disable scoping for specific features if needed
   */
  scopedByUser?: boolean;
}

/**
 * Default persistence configuration
 */
export const DEFAULT_PERSISTENCE_CONFIG: FeaturePersistenceConfig = {
  enabled: false,
  storage: storage,
  mergeStrategy: 'merge',
  debounce: 300,
  timeout: 5000,
  scopedByUser: true, // Default to scoped storage for security
};

// Add mergeStrategy to PersistConfig for typing
interface EnhancedPersistConfig<S> extends PersistConfig<S> {
  mergeStrategy?: 'merge' | 'deepMerge' | 'replace';
}

/**
 * Creates a redux-persist configuration from a feature persistence config
 */
export function createPersistConfig(
  featureId: string,
  config: FeaturePersistenceConfig,
): EnhancedPersistConfig<any> {
  // Generate the storage key with feature prefix
  const storageKey = `feature_${config.key || featureId}`;

  // Determine if we should scope by user (default to true unless explicitly disabled)
  const shouldScope = config.scopedByUser !== false;

  if (process.env.NODE_ENV !== 'production') {
    console.log(`[ScopedPersist] createPersistConfig for ${featureId}:`, {
      shouldScope,
      storageKey,
      usingScopedStorage: shouldScope,
    });
  }

  // Prepare transforms array (only feature-specific transforms, no owner guard needed)
  // Note: Owner-based scoping is handled by the scoped storage wrapper, not transforms
  const transforms = config.transforms ? [...config.transforms] : [];

  const persistConfig: EnhancedPersistConfig<any> = {
    key: storageKey,
    // Use scoped storage wrapper (handles dynamic scoping at runtime)
    // or custom storage if provided (for features that need special handling)
    storage: config.storage ? createScopedStorage(config.storage) : scopedStorage,
    whitelist: config.whitelist,
    blacklist: config.blacklist,
    transforms,
    version: config.version,
    debug: process.env.NODE_ENV !== 'production',
    timeout: config.timeout,
    mergeStrategy: config.mergeStrategy,
  };

  // Add migration function if migrations are provided
  if (config.migrations) {
    persistConfig.migrate = (state: any, currentVersion: number) => {
      let newState = { ...state };

      // Apply migrations in order
      for (let v = currentVersion; v < (config.version || 0); v++) {
        if (config.migrations?.[v]) {
          newState = config.migrations[v](newState);
        }
      }

      return Promise.resolve(newState);
    };
  }

  // Add state reconciler
  persistConfig.stateReconciler = (inboundState, originalState, reducedState, config) => {
    // Handle different merge strategies
    if (persistConfig.mergeStrategy === 'deepMerge') {
      return deepMerge(originalState, inboundState);
    } else if (persistConfig.mergeStrategy === 'replace') {
      return inboundState;
    } else {
      // Default to shallow merge
      return { ...reducedState, ...inboundState };
    }
  };

  return persistConfig;
}

/**
 * Helper function for deep merging objects
 */
function deepMerge(target: any, source: any) {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key];
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        output[key] = source[key];
      }
    });
  }

  return output;
}

/**
 * Helper function to check if value is an object
 */
function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Create a dictionary of feature persistence reducers using nested persist pattern
 * This applies persistReducer to each reducer with its own configuration
 */
export function createFeaturePersistReducers(
  reducers: Record<string, any>,
  featureId: string,
  persistConfig: FeaturePersistenceConfig,
): Record<string, any> {
  // If persistence is not enabled, return original reducers
  if (!persistConfig.enabled) {
    console.log(`Feature ${featureId} persistence disabled, using original reducers`);
    return reducers;
  }

  console.log(`Creating nested persist reducers for feature ${featureId}:`, Object.keys(reducers));

  const persistedReducers: Record<string, any> = {};

  // Process each reducer with its own nested persistence config
  Object.entries(reducers).forEach(([key, reducer]) => {
    // Create a nested persist config for this specific reducer slice
    const nestedConfig = createPersistConfig(`${featureId}_${key}`, {
      ...persistConfig,
      key: `${featureId}_${key}`, // Unique key for this reducer slice
    });

    // Apply nested persistence to this reducer
    persistedReducers[key] = persistReducer(nestedConfig, reducer);

    console.log(`✅ Applied nested persistence to ${key} with storage key: ${nestedConfig.key}`);
  });

  return persistedReducers;
}

// Need to import persistReducer at runtime to avoid circular dependency
// This is a hack to make TypeScript happy
let _persistReducer: any = null;

export function setPersistReducer(persistReducerFn: any) {
  _persistReducer = persistReducerFn;
}

export function persistReducer<
  S = any,
  A extends import('redux').Action = import('redux').AnyAction,
>(
  config: PersistConfig<any>,
  reducer: import('redux').Reducer<S, A>,
): import('redux').Reducer<S & import('redux-persist/es/persistReducer').PersistPartial, A> {
  if (!_persistReducer) {
    throw new Error('persistReducer not initialized! Call setPersistReducer first.');
  }
  return _persistReducer(config, reducer);
}
